/**
 * i18n (architecture §6.2, FE-03): Spanish es-CR is the default and the source catalog; English is
 * the second language. Keys are English and semantic. `common` and the UI kit's `ui` (FE-03
 * FR-FE03-05, `packages/frontend/ui/locales`) are bundled; every other namespace is a lazy chunk per
 * language (FR-FE03-31) that routes load in `beforeLoad` via `loadNamespaces`.
 */
import i18n, { type BackendModule, type ResourceKey } from "i18next";
import { initReactI18next } from "/home/runner/work/sosdigital-pindrop/sosdigital-pindrop/node_modules/.pnpm/react-i18next@17.1.8_i18next@26.5.10_typescript@6.0.3__react-dom@18.3.1_react@18.3.1__react@18.3.1_typescript@6.0.3/node_modules/react-i18next/dist/es/initReactI18next.js";

import { reportOnce } from "./observability";

export const LANGUAGES = ["es-CR", "en"] as const;
export type Language = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = "es-CR";
export const NAMESPACES = [
  "common",
  "ui",
  "auth",
  "seller",
  "owner",
  "admin",
  "pay",
  "public",
  "map",
  "sites",
  "editor",
  "leads",
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const LOCALE_KEY = "sos.locale";
export const LOCALE_EXPLICIT_KEY = "sos.locale.explicit";

// Only the default language's `common` and `ui` ship in the first-load JS (NFR-FE01-01 budget): an
// English first visit loads its two small catalogs before the first render (`useSuspense`).
const bundled = import.meta.glob<ResourceKey>("../locales/es-CR/common.json", {
  eager: true,
  import: "default",
});
const bundledUi = import.meta.glob<ResourceKey>(
  "../../../../packages/frontend/ui/locales/es-CR/ui.json",
  {
    eager: true,
    import: "default",
  },
);
const lazy = import.meta.glob<ResourceKey>(
  ["../locales/*/*.json", "!../locales/es-CR/common.json"],
  {
    import: "default",
  },
);
const lazyUi = import.meta.glob<ResourceKey>(
  [
    "../../../../packages/frontend/ui/locales/*/ui.json",
    "!../../../../packages/frontend/ui/locales/es-CR/ui.json",
  ],
  { import: "default" },
);

const lazyBackend: BackendModule = {
  type: "backend",
  init() {},
  read(language, namespace, callback) {
    const load =
      namespace === "ui"
        ? lazyUi[`../../../../packages/frontend/ui/locales/${language}/ui.json`]
        : lazy[`../locales/${language}/${namespace}.json`];
    if (!load) {
      callback(new Error(`no catalog ${language}/${namespace}`), false);
      return;
    }
    load().then(
      (data) => callback(null, data),
      (error: Error) => callback(error, false),
    );
  },
};

export function isLanguage(value: unknown): value is Language {
  return (LANGUAGES as readonly unknown[]).includes(value);
}

/** FR-FE03-02: es, es-* → es-CR; en, en-* → en; anything else → no match. */
export function mapBrowserTag(tag: string | undefined): Language | null {
  const lower = (tag ?? "").toLowerCase();
  if (lower === "en" || lower.startsWith("en-")) return "en";
  if (lower === "es" || lower.startsWith("es-")) return "es-CR";
  return null;
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Private mode: the choice lasts for this session only.
  }
}

/**
 * Detection without a session (FR-FE03-14 steps 2–4): saved `sos.locale` → first browser language
 * if it is English → es-CR. The session's `me.user.locale` is applied later (`syncLocaleWithMe`).
 */
export function detectLanguage(): Language {
  const stored = readStorage(LOCALE_KEY);
  if (isLanguage(stored)) return stored;
  const first =
    typeof navigator !== "undefined" ? (navigator.languages?.[0] ?? navigator.language) : "";
  return mapBrowserTag(first) === "en" ? "en" : DEFAULT_LANGUAGE;
}

export function isExplicitLanguage(): boolean {
  return readStorage(LOCALE_EXPLICIT_KEY) === "true";
}

export function clearExplicitLanguage(): void {
  writeStorage(LOCALE_EXPLICIT_KEY, null);
}

/**
 * Switch language: load the namespaces in use first (no flash of keys), then change, update
 * `<html lang>` and save the device preference. `explicit` marks a user choice (FR-FE03-18).
 */
export async function setLanguage(lang: Language, options: { explicit?: boolean } = {}) {
  await i18n.loadLanguages(lang);
  // §8: offline with a namespace of that language not cached → keep the current language.
  const inUse = (i18n.reportNamespaces?.getUsedNamespaces() ?? []).filter((ns) =>
    (NAMESPACES as readonly string[]).includes(ns),
  );
  const missing = inUse.filter((ns) => !i18n.hasResourceBundle(lang, ns));
  if (missing.length) throw new LanguageUnavailableError(lang, missing);
  await i18n.changeLanguage(lang);
  writeStorage(LOCALE_KEY, lang);
  if (options.explicit) writeStorage(LOCALE_EXPLICIT_KEY, "true");
  document.documentElement.lang = lang;
}

/** The catalogs of a language could not be loaded (offline, FE-03 §8). */
export class LanguageUnavailableError extends Error {
  constructor(
    readonly language: Language,
    readonly namespaces: string[],
  ) {
    super(`catalogs not available for ${language}: ${namespaces.join(", ")}`);
    this.name = "LanguageUnavailableError";
  }
}

export function currentLanguage(): Language {
  return isLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE;
}

/** Await before rendering a route that uses these namespaces (FR-FE03-31). */
export async function loadNamespaces(...namespaces: Namespace[]): Promise<void> {
  await i18n.loadNamespaces(namespaces);
}

const resources: Record<string, Record<string, ResourceKey>> = {};
for (const [path, data] of Object.entries(bundled)) {
  const lang = path.split("/").at(-2);
  if (lang) resources[lang] = { ...resources[lang], common: data };
}
for (const [path, data] of Object.entries(bundledUi)) {
  const lang = path.split("/").at(-2);
  if (lang) resources[lang] = { ...resources[lang], ui: data };
}

const DEV = import.meta.env.DEV || import.meta.env.MODE === "test";

/**
 * FR-FE03-04: a key missing everywhere logs a console warning (with namespace) and shows as
 * `[ns:key]` in development; in staging/production it is reported once per key per session
 * (keys only, no PII: NFR-FE03-05) and the es-CR fallback is shown.
 */
export function onMissingKey(namespaces: readonly string[] | string, key: string): void {
  const ns = Array.isArray(namespaces) ? (namespaces[0] ?? "common") : String(namespaces);
  if (DEV) console.warn(`i18n: missing key ${ns}:${key}`);
  else reportOnce(`i18n:${ns}:${key}`, `i18n.missing_key ${ns}:${key}`);
}

export function missingKeyText(key: string, ns?: string): string {
  if (!DEV) return key;
  return key.includes(":") ? `[${key}]` : `[${ns ?? "common"}:${key}]`;
}

/**
 * Pseudo-locale (FE-03 §11 Q3, NFR-FE03-03): in non-production builds, `localStorage["sos.pseudo"]
 * = "1"` stretches every string by 35 % with accented letters, to catch truncation and hard-coded
 * text. Never active in production.
 */
export function pseudoLocalize(value: string): string {
  const map: Record<string, string> = {
    a: "á",
    e: "é",
    i: "í",
    o: "ó",
    u: "ú",
    n: "ñ",
    A: "Á",
    E: "É",
    O: "Ó",
  };
  const parts = value.split(/(\{\{[^}]+\}\}|<[^>]+>)/);
  const accented = parts.map((part, i) =>
    i % 2 ? part : part.replace(/[aeiounAEO]/g, (c) => map[c] ?? c),
  );
  const text = accented.join("");
  const pad = Math.ceil(value.length * 0.35);
  return pad > 0 ? `${text} ${"·".repeat(Math.max(1, pad - 1))}` : text;
}

const pseudo =
  import.meta.env.VITE_ENV !== "production" && readStorage("sos.pseudo") === "1"
    ? [
        {
          type: "postProcessor" as const,
          name: "pseudo",
          process: (value: string) => pseudoLocalize(value),
        },
      ]
    : [];

let instance = i18n.use(lazyBackend).use(initReactI18next);
for (const processor of pseudo) instance = instance.use(processor);

void instance.init({
  resources,
  partialBundledLanguages: true,
  lng: detectLanguage(),
  supportedLngs: [...LANGUAGES],
  fallbackLng: DEFAULT_LANGUAGE,
  load: "currentOnly",
  ns: ["common", "ui"],
  defaultNS: "common",
  fallbackNS: ["common", "ui"],
  saveMissing: true,
  missingKeyHandler: (_lngs, ns, key) => onMissingKey(ns, key),
  // The parser receives "ns:key" so the bracketed text names the namespace.
  appendNamespaceToMissingKey: true,
  parseMissingKeyHandler: (key) => missingKeyText(key),
  postProcess: pseudo.length ? ["pseudo"] : false,
  nsSeparator: ":",
  keySeparator: ".",
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: false,
  react: { useSuspense: true },
});

if (typeof document !== "undefined") {
  document.documentElement.lang = currentLanguage();
  document.documentElement.dir = "ltr";
}

i18n.on("languageChanged", (lang) => {
  if (typeof document !== "undefined" && isLanguage(lang)) document.documentElement.lang = lang;
});

// §8: a language change in another tab is applied here too.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (
      event.key === LOCALE_KEY &&
      isLanguage(event.newValue) &&
      event.newValue !== i18n.language
    ) {
      void setLanguage(event.newValue).catch(() => undefined);
    }
  });
}

export default i18n;
